import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COLORS, HEADER_COLORS, DARK_THEME } from '../../constants/colors';
import { LOGOS } from '../../constants/logos';
import { FLAGS } from '../../constants/flags';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { language, changeLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsLangOpen(false);
    };
    if (isLangOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isLangOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const languages = [
    { code: 'en', name: 'English', flag: FLAGS.en },
    { code: 'tr', name: 'Türkçe', flag: FLAGS.turkey },
  ];

  const navItems = language === 'tr' ? [
    { label: 'Ana Sayfa', path: '/' },
    { label: 'Hakkında', path: '/about' },
    { label: 'Üye Devletler', path: '/member-states' },
    { label: 'Harita', path: '/map' },
    { label: 'Haberler', path: '/news' },
    { label: 'İletişim', path: '/contact' },
  ] : [
    { label: 'Home', path: '/' },
    { label: 'About OTS', path: '/about' },
    { label: 'Member States', path: '/member-states' },
    { label: 'Map', path: '/map' },
    { label: 'News & Events', path: '/news' },
    { label: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const currentLang = languages.find(l => l.code === language);
  const isHomePage = location.pathname === '/';
  const isMapPage = location.pathname === '/map';
  const isAboutPage = location.pathname === '/about';
  const isNewsPage = location.pathname === '/news';
  const isContactPage = location.pathname === '/contact';
  const isMemberStatesPage = location.pathname === '/member-states';
  const isTransparentPage = isHomePage || isMapPage || isAboutPage || isNewsPage || isContactPage || isMemberStatesPage;

  const handleLangSelect = (code) => {
    changeLanguage(code);
    setIsLangOpen(false);
  };

  // Home ve Map sayfasında ve scroll edilmediğinde transparan header
  const getHeaderBackground = () => {
    if (isTransparentPage && !isScrolled) {
      return 'transparent';
    }
    return isDarkMode 
      ? (isScrolled ? DARK_THEME.backgroundSecondary : DARK_THEME.background)
      : (isScrolled ? HEADER_COLORS.backgroundScrolled : HEADER_COLORS.background);
  };

  const headerBg = getHeaderBackground();

  return (
    <header 
      className={`header ${isScrolled ? 'scrolled' : ''}`}
      style={{
        backgroundColor: headerBg,
        boxShadow: (isTransparentPage && !isScrolled) ? 'none' : (isScrolled ? `0 4px 20px ${isDarkMode ? DARK_THEME.shadow : HEADER_COLORS.shadow}` : 'none'),
        borderBottom: (isTransparentPage && !isScrolled) ? 'none' : `1px solid ${isDarkMode ? DARK_THEME.border : HEADER_COLORS.border}`,
        transition: 'all 0.3s ease',
      }}
    >
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="header-logo">
          <img 
            src={isDarkMode ? LOGOS.dark : LOGOS.light} 
            alt="Turkic Silk Road" 
            className="header-logo-image" 
          />
          <div className="header-logo-text">
            <span className="header-logo-title" style={{ color: isDarkMode ? DARK_THEME.primary : COLORS.white }}>
              Turkic Silk Road
            </span>
            <span className="header-logo-subtitle" style={{ color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255, 255, 255, 0.8)' }}>
              Organization of Turkic States
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="header-nav">
          {navItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              className="header-nav-link"
              style={{
                backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                fontWeight: isActive(item.path) ? '600' : '500',
                color: isDarkMode ? DARK_THEME.textPrimary : COLORS.white,
              }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right Section */}
        <div className="header-right">
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleTheme}
            className="header-theme-toggle"
            style={{
              backgroundColor: isDarkMode ? DARK_THEME.surfaceHover : 'rgba(255, 255, 255, 0.15)',
            }}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>

          {/* Desktop Language Selector */}
          <div className="header-lang-container header-lang-desktop">
            <button
              className="header-lang-button"
              style={{
                backgroundColor: isDarkMode ? DARK_THEME.surfaceHover : 'rgba(255, 255, 255, 0.15)',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setIsLangOpen(!isLangOpen);
              }}
            >
              {currentLang?.flag && (
                <img src={currentLang.flag} alt={currentLang.name} className="header-lang-flag" />
              )}
              <span className="header-lang-code" style={{ color: isDarkMode ? DARK_THEME.textPrimary : COLORS.white }}>
                {currentLang?.code.toUpperCase()}
              </span>
              <span className="header-lang-arrow" style={{ color: isDarkMode ? DARK_THEME.textSecondary : 'rgba(255,255,255,0.8)' }}>
                {isLangOpen ? '▲' : '▼'}
              </span>
            </button>

            {isLangOpen && (
              <div 
                className="header-lang-dropdown"
                style={{
                  backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
                  boxShadow: isDarkMode ? '0 10px 40px rgba(0,0,0,0.5)' : '0 10px 40px rgba(0, 0, 0, 0.2)',
                }}
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    className="header-lang-option"
                    style={{
                      backgroundColor: language === lang.code 
                        ? (isDarkMode ? DARK_THEME.primary + '30' : COLORS.primary + '20')
                        : 'transparent',
                      color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLangSelect(lang.code);
                    }}
                  >
                    {lang.flag && (
                      <img src={lang.flag} alt={lang.name} className="header-lang-option-flag" />
                    )}
                    <span className="header-lang-option-name">{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="header-menu-button"
            style={{
              backgroundColor: isDarkMode ? DARK_THEME.surfaceHover : 'rgba(255, 255, 255, 0.2)',
            }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="header-menu-icon" style={{ color: isDarkMode ? DARK_THEME.textPrimary : COLORS.white }}>
              {isMenuOpen ? '✕' : '☰'}
            </span>
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div 
            className="header-mobile-overlay"
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Mobile Menu */}
        <div 
          className={`header-mobile-menu ${isMenuOpen ? 'open' : ''}`}
          style={{
            backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
          }}
        >
          {navItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              className="header-mobile-nav-link"
              style={{
                backgroundColor: isActive(item.path) 
                  ? (isDarkMode ? DARK_THEME.primary + '20' : 'rgba(0, 171, 194, 0.15)')
                  : 'transparent',
                color: isActive(item.path) 
                  ? (isDarkMode ? DARK_THEME.primary : COLORS.primary)
                  : (isDarkMode ? DARK_THEME.textPrimary : COLORS.dark),
              }}
              onClick={() => setIsMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}

          {/* Mobile Theme Toggle */}
          <div 
            className="header-mobile-controls"
            style={{
              borderTop: `1px solid ${isDarkMode ? DARK_THEME.border : COLORS.gray300}`,
            }}
          >
            <button 
              onClick={toggleTheme} 
              className="header-mobile-theme-toggle"
              style={{
                backgroundColor: isDarkMode ? DARK_THEME.surfaceHover : COLORS.light,
                color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark,
              }}
            >
              {isDarkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
            </button>
          </div>

          {/* Mobile Language Selector */}
          <div 
            className="header-mobile-lang-section"
            style={{
              borderTop: `1px solid ${isDarkMode ? DARK_THEME.border : COLORS.gray300}`,
            }}
          >
            <span 
              className="header-mobile-lang-title"
              style={{
                color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray600,
              }}
            >
              {language === 'tr' ? 'Dil' : 'Language'}
            </span>
            <div className="header-mobile-lang-grid">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className="header-mobile-lang-option"
                  style={{
                    backgroundColor: language === lang.code 
                      ? (isDarkMode ? DARK_THEME.primary : COLORS.primary)
                      : (isDarkMode ? DARK_THEME.surfaceHover : COLORS.light),
                    color: language === lang.code 
                      ? COLORS.white 
                      : (isDarkMode ? DARK_THEME.textPrimary : COLORS.dark),
                  }}
                  onClick={() => handleLangSelect(lang.code)}
                >
                  {lang.flag && (
                    <img src={lang.flag} alt={lang.name} className="header-mobile-lang-flag" />
                  )}
                  <span className="header-mobile-lang-code">{lang.code.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
